/**
 * DualAxisChart - Two metrics with different scales
 *
 * Usage:
 *   <DualAxisChart
 *     title="Sleep vs Calories"
 *     data={{
 *       points: [{ date: '2024-01-01', sleep: 7.2, calories: 2100 }, ...],
 *       leftAxis: 'sleep',
 *       rightAxis: 'calories',
 *       leftLabel: 'Sleep (hrs)',
 *       rightLabel: 'Calories',
 *     }}
 *   />
 */

import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { BlockProps, DualAxisData } from './types';
import { CHART_COLORS } from './colors';
import './blocks.css';

export interface DualAxisChartProps extends BlockProps<DualAxisData> {
  /** Chart height in pixels */
  height?: number;
  /** Show right axis as bars instead of line */
  rightAsBars?: boolean;
}

export function DualAxisChart({
  data,
  title,
  config,
  height = 280,
  rightAsBars = false,
  className,
}: DualAxisChartProps) {
  const { points, leftAxis, rightAxis, leftLabel, rightLabel } = data;

  if (!points || points.length === 0) {
    return (
      <div className={`block-chart ${className || ''}`}>
        {title && <div className="block-chart-title">{title}</div>}
        <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--color-text-faint)' }}>
          No data available
        </div>
      </div>
    );
  }

  const leftColor = config?.colors?.[0] || CHART_COLORS[0];
  const rightColor = config?.colors?.[1] || CHART_COLORS[1];

  // Format date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;

    return (
      <div style={{
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: '6px',
        padding: '8px 12px',
        boxShadow: 'var(--popover-shadow, 0 2px 4px rgba(0,0,0,0.1))',
      }}>
        <p style={{
          color: 'var(--color-text-subtle)',
          fontWeight: 600,
          margin: '0 0 8px 0',
          fontSize: '13px',
        }}>
          {formatDate(label)}
        </p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{
            color: entry.color,
            margin: '4px 0',
            fontSize: '14px',
          }}>
            {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className={`block-chart ${className || ''}`}>
      {title && <div className="block-chart-title">{title}</div>}

      <div className="block-chart-container">
        <ResponsiveContainer width="100%" height={height}>
          <ComposedChart data={points} margin={{ top: 5, right: 50, bottom: 5, left: 0 }}>
            <CartesianGrid
              strokeDasharray="0"
              stroke="var(--color-border)"
              strokeWidth={1}
              horizontal={true}
              vertical={false}
            />

            <XAxis
              dataKey="date"
              stroke="var(--color-text-faint)"
              tick={{ fill: 'var(--color-text-faint)', fontSize: 10 }}
              axisLine={{ stroke: 'var(--color-border-strong)', strokeWidth: 1 }}
              tickLine={false}
              tickFormatter={formatDate}
            />

            {/* Left Y-axis */}
            <YAxis
              yAxisId="left"
              stroke={leftColor}
              tick={{ fill: leftColor, fontSize: 10 }}
              axisLine={{ stroke: leftColor, strokeWidth: 1 }}
              tickLine={false}
              tickFormatter={(value) => value.toLocaleString()}
              label={leftLabel ? {
                value: leftLabel,
                angle: -90,
                position: 'insideLeft',
                fill: leftColor,
                fontSize: 11,
              } : undefined}
            />

            {/* Right Y-axis */}
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke={rightColor}
              tick={{ fill: rightColor, fontSize: 10 }}
              axisLine={{ stroke: rightColor, strokeWidth: 1 }}
              tickLine={false}
              tickFormatter={(value) => value.toLocaleString()}
              label={rightLabel ? {
                value: rightLabel,
                angle: 90,
                position: 'insideRight',
                fill: rightColor,
                fontSize: 11,
              } : undefined}
            />

            <Tooltip content={<CustomTooltip />} />
            <Legend />

            {/* Left axis line */}
            <Line
              yAxisId="left"
              type="monotone"
              dataKey={leftAxis}
              name={leftLabel || leftAxis.replace(/_/g, ' ')}
              stroke={leftColor}
              strokeWidth={2}
              dot={{ fill: leftColor, strokeWidth: 2, r: 3 }}
              activeDot={{ r: 5 }}
              connectNulls
            />

            {/* Right axis - line or bars */}
            {rightAsBars ? (
              <Bar
                yAxisId="right"
                dataKey={rightAxis}
                name={rightLabel || rightAxis.replace(/_/g, ' ')}
                fill={rightColor}
                fillOpacity={0.6}
                radius={[4, 4, 0, 0]}
                maxBarSize={30}
              />
            ) : (
              <Line
                yAxisId="right"
                type="monotone"
                dataKey={rightAxis}
                name={rightLabel || rightAxis.replace(/_/g, ' ')}
                stroke={rightColor}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ fill: rightColor, strokeWidth: 2, r: 3 }}
                activeDot={{ r: 5 }}
                connectNulls
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default DualAxisChart;
